import * as React from 'react';
import FileSaver from 'file-saver';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { localizeDate } from '@pec/aion-ui-i18next/helpers/localize';
import Accordion from '@material-ui/core/Accordion';
import { AccordionDetails, AccordionSummary, createStyles, Divider, makeStyles, Typography } from '@material-ui/core';
import { ChevronRight } from '@material-ui/icons';
import { Certification } from './Certification';
import { Widget } from '../Widget';
import { useAppDispatch, useTypedSelector } from '../../combineReducers';
import { shouldHideRefreshButton } from '../../helpers/certificationHelpers';
import { downloadDocument } from '../../slices/documents';
import { fetchSubscriptions } from '../../slices/subscriptionsV2';
import { regenerateCertificate } from '../../slices/regenerateCertificate';
import { fetchCertificationDocuments } from '../../slices/certificationDocuments';
import { fetchCertifications } from '../../slices/certifications';
import {
  CertificationStatus,
  CertificationType,
  ICertification,
  // ICertificationDocument,
  VerificationCategory
} from '../../interfaces/certifications';
import { usePermission } from '../../hooks/usePermission';
import { getFileNameFromContentDisposition } from '../../helpers/helpers';

type RouteParams = {
  organizationId: string;
};

type AccordionGroupData = {
  key: string;
  title: string;
  expiryDate?: string;
  certifications: (ICertification & { isExpired: boolean })[];
};

const useStyles = makeStyles(() =>
  createStyles({
    membershipSummary: {
      flexDirection: 'row-reverse'
    },
    certifications: {
      flexDirection: 'column',
      '& > :first-child': {
        paddingTop: '0 !important'
      }
    },

    accordionRoot: {
      boxShadow: 'none',
      border: '0 !important'
    },
    accordion: {
      marginBottom: 0,
      boxShadow: 'none !important',

      '&:before': {
        display: 'none'
      },

      '&.Mui-expanded': {
        margin: '0 !important',
        boxShadow: 'none !important'
      }
    },

    accordionSummary: {
      fontSize: 14,
      flexDirection: 'row-reverse',
      minHeight: '48px !important',
      padding: '0 !important',

      '&, &.Mui-expanded': {
        minHeight: '48px !important',
        '& .MuiAccordionSummary-content': {
          margin: '12px 0 !important'
        },
        '& .MuiAccordionSummary-expandIcon': {
          margin: '0 8px 0 0',
          padding: '0 !important',
          transition: 'transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms'
        }
      },

      '&.Mui-expanded .MuiAccordionSummary-expandIcon': {
        transform: 'rotate(90deg)'
      }
    },

    groupTitle: {
      fontWeight: 500
    },
    expiryDate: {
      color: '#999',
      fontSize: 12,
      marginLeft: 8,
      fontWeight: 'unset'
    },
    expiryDateExpired: {
      color: '#FF0000',
      fontSize: 12,
      marginLeft: 8,
      fontWeight: 'unset'
    }
  })
);

const useProcessedCertificationData = (
  certifications: ICertification[],
  subscriptions: any[],
  t: (key: string, defaultValue?: string) => string
): AccordionGroupData[] => {
  return React.useMemo(() => {
    const certificationsItems = certifications.map(certification => {
      const matching = subscriptions.find(subscription => subscription.id === certification.subscriptionId);
      // moment used for date comparison logic only — not for display formatting
      const isExpired = matching?.expirationDate ? moment(matching.expirationDate).isBefore() : false;
      return { ...certification, isExpired };
    });

    const groups: { [key: string]: typeof certificationsItems } = {};
    for (const certification of certificationsItems) {
      const subscriptionId = certification.subscriptionId;
      if (!groups[subscriptionId]) {
        groups[subscriptionId] = [];
      }
      groups[subscriptionId].push(certification);
    }

    const regularGroups: AccordionGroupData[] = [];
    let addonCertifications: typeof certificationsItems = [];

    for (const [, certs] of Object.entries(groups)) {
      if (certs.length === 0) continue;

      const hasAddonType = certs.some(cert => cert.operationType === 'Addon');

      if (hasAddonType) {
        addonCertifications.push(...certs.sort((a, b) => a.name.localeCompare(b.name)));
      } else {
        const firstCert = certs[0];
        const title =
          `${firstCert.subscriptionType} ` + t('newDashboard.certifications.groupTitleSuffix', 'Membership');
        const expirationMoment = moment(firstCert.subscriptionExpirationDate);
        const isExpired = expirationMoment.isBefore(moment());
        const expiryPrefix = isExpired ? 'Expired' : 'Expires';
        const expiryDate = `${expiryPrefix} ${localizeDate(firstCert.subscriptionExpirationDate, t)}`;

        regularGroups.push({
          key: firstCert.subscriptionId,
          title,
          expiryDate,
          certifications: certs
        });
      }
    }

    if (addonCertifications.length > 0) {
      regularGroups.push({
        key: 'other-certifications',
        title: t('newDashboard.certifications.otherCertificationGroupTitle', 'Other certification'),
        certifications: addonCertifications
      });
    }

    const allGroupsExpired = regularGroups.every(group => {
      if (!group.expiryDate) return false;
      return group.expiryDate.startsWith('Expired');
    });

    if (allGroupsExpired) {
      regularGroups.forEach(group => {
        group.certifications.forEach(cert => {
          cert.isCurrentMembershipExpired =
            cert.status === CertificationStatus.Active || cert.status === CertificationStatus.Inactive;
        });
      });
    }

    const filteredGroups = regularGroups
      .map(group => {
        const filteredCertifications = group.certifications.filter(cert => {
          if (cert.name === CertificationType.ChasAdvanced && cert.subscriptionType === CertificationType.ChasElite) {
            return false;
          }
          return true;
        });

        return {
          ...group,
          certifications: filteredCertifications
        };
      })
      .filter(group => group.certifications.length > 0);

    return filteredGroups;
  }, [certifications, subscriptions]); // eslint-disable-line react-hooks/exhaustive-deps
};

export const Certifications: React.FC = () => {
  const classes = useStyles();
  const { organizationId } = useParams<RouteParams>();
  const { t } = useTranslation(['subscriber', 'formats']);
  const dispatch = useAppDispatch();
  const { hasAccreditationsPermission } = usePermission();
  const [expandedAccordions, setExpandedAccordions] = React.useState<number[]>([]);
  const [isUpdating, setIsUpdating] = React.useState<boolean>(false);

  const { isFetching: isFetchingCertifications, error, certifications } = useTypedSelector(
    state => state.certifications
  );
  const { certificationDocuments, isFetching: isFetchingCertificationDocuments } = useTypedSelector(
    state => state.certificationDocuments
  );
  const { subscriptions, isFetching: isFetchingSubscriptions } = useTypedSelector(state => state.subscriptionsV2);
  const { mainSubscription } = useTypedSelector(state => state.mainSubscription);
  const accordionData = useProcessedCertificationData(certifications, subscriptions, t);
  //Store certificationDocuments in a ref
  const certificationDocumentsRef = React.useRef(certificationDocuments);

  React.useEffect(() => {
    certificationDocumentsRef.current = certificationDocuments;
  }, [certificationDocuments]);

  React.useEffect(() => {
    dispatch(fetchCertifications(organizationId));
    dispatch(fetchCertificationDocuments(organizationId));
    dispatch(fetchSubscriptions(organizationId));
  }, [dispatch, organizationId]);

  React.useEffect(() => {
    const expandedAccordions: number[] = [];
    const activeCertificationGroups = accordionData.reduce((acc, group, index) => {
      if (group.certifications.some(certification => certification.status === CertificationStatus.Active)) {
        acc.push(index);
      }
      return acc;
    }, [] as number[]);

    if (
      accordionData.length === 1 ||
      activeCertificationGroups.includes(0) ||
      (accordionData.length > 1 && !activeCertificationGroups.some(index => index > 0))
    ) {
      expandedAccordions.push(0);
    }
    expandedAccordions.push(...activeCertificationGroups.filter(index => index !== 0));

    setExpandedAccordions(expandedAccordions);
  }, [accordionData]);

  const handleAccordionChange = (panelIndex: number) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordions(prev => (isExpanded ? [...prev, panelIndex] : prev.filter(index => index !== panelIndex)));
  };

  const handleDownload = (certification: ICertification) => () => {
    // Get FRESH certificationDocuments from ref at CLICK time
    const certificationDocument = certificationDocumentsRef.current.find(
      doc => doc.accreditationId === certification.id
    );

    if (!certificationDocument) return;

    const { name, verificationCategory } = certification;
    const { documentReferenceId } = certificationDocument;
    const certificateName = name
      .replace('CHAS', '')
      .trim()
      .replace(/ /g, '_');
    const documentName = `Certificate_${certificateName}_${
      verificationCategory === VerificationCategory.Assured ? 'Assured_' : ''
    }${mainSubscription.countryISO2}`;

    dispatch(
      downloadDocument({
        documentReferenceId,
        afterSuccess: ({ data, headers }) => {
          const blob = new Blob([data], { type: headers['content-type'] });
          const originalFileName = getFileNameFromContentDisposition(headers['content-disposition']);
          FileSaver.saveAs(blob, `${documentName}.${originalFileName.extension}`);
        }
      })
    );
  };

  const handleRegenerate = (accreditationId: string) => {
    dispatch(
      regenerateCertificate({
        accreditationId,
        afterSuccess: () => {
          setIsUpdating(true);
          setTimeout(() => {
            setIsUpdating(false);
            dispatch(fetchCertificationDocuments(organizationId));
          }, 4000);
        }
      })
    );
  };

  // Render Guards
  if (!hasAccreditationsPermission || (certifications.length === 0 && !isFetchingCertifications)) {
    return null;
  }

  // Render
  return (
    <Widget
      title={t('newDashboard.certifications.title', 'Certifications')}
      isFetching={isFetchingCertifications || isFetchingSubscriptions || isFetchingCertificationDocuments || isUpdating}
      hasFetchingError={!!error}
    >
      {accordionData.map((group: AccordionGroupData, index: number) => (
        <React.Fragment key={group.key}>
          <Accordion
            className={classes.accordion}
            expanded={expandedAccordions.includes(index)}
            onChange={handleAccordionChange(index)}
          >
            <AccordionSummary
              expandIcon={<ChevronRight />}
              aria-controls={`panel${index}-content`}
              id={`panel${index}-header`}
              className={classes.accordionSummary}
            >
              <Typography className={classes.groupTitle}>{group.title}</Typography>
              {group.expiryDate && (
                <Typography>
                  <span
                    className={group.expiryDate.startsWith('Expired') ? classes.expiryDateExpired : classes.expiryDate}
                  >
                    {group.expiryDate}
                  </span>
                </Typography>
              )}
            </AccordionSummary>
            <AccordionDetails className={classes.certifications}>
              {group.certifications.map(certification => {
                return (
                  <Certification
                    key={certification.id}
                    certification={certification}
                    shouldHideRefresh={shouldHideRefreshButton(certification, certifications)}
                    onDownloadClick={handleDownload(certification)}
                    onRefreshClick={handleRegenerate}
                  />
                );
              })}
            </AccordionDetails>
          </Accordion>
          {index < accordionData.length - 1 && <Divider />}
        </React.Fragment>
      ))}
    </Widget>
  );
};
